// v1.1.0 · Proyectos reales, cada uno con una mini interfaz dibujada en código que muestra el tipo de sistema
// v1.3.0 · Índice con pestañas: se elige el proyecto y se ve su sistema (teclado: flechas, Inicio, Fin)
// v1.5.0 · 5 pantallas animadas por proyecto; cambio de proyecto con deslizamiento y desenfoque
import React, { useRef, useState } from 'react';
import { ExternalLink, MessageCircle } from 'lucide-react';
import { projects, contactData } from '../../data/site';
import BackgroundMesh from './BackgroundMesh';
import { Screens } from './dash/kit';
import { alfaScreens } from './dash/alfa';
import { difareScreens } from './dash/difare';
import { turismoScreens } from './dash/turismo';
import { neuroScreens } from './dash/neuro';
import { retailScreens } from './dash/retail';
import { crmScreens } from './dash/crm';

// v1.5.0 · Cada proyecto muestra 5 pantallas animadas de su sistema (carpeta dash/)
const SCREENS = {
  ot: alfaScreens, precios: difareScreens, despacho: turismoScreens, clase: neuroScreens, inventario: retailScreens, crm: crmScreens,
};

/* ───────── Sección ───────── */
export default function SiteProjects() {
  const [sel, setSel] = useState(0);
  const tabs = useRef([]);
  const p = projects[sel];

  const onKey = (e) => {
    const last = projects.length - 1;
    const next = { ArrowDown: sel + 1, ArrowRight: sel + 1, ArrowUp: sel - 1, ArrowLeft: sel - 1, Home: 0, End: last }[e.key];
    if (next === undefined) return;
    e.preventDefault();
    const i = next < 0 ? last : next > last ? 0 : next;
    setSel(i);
    tabs.current[i]?.focus();
  };

  return (
    <section id="proyectos" data-scan className="relative scroll-mt-24 overflow-hidden bg-[var(--night)] py-20 text-white lg:py-28">
      <BackgroundMesh dark />
      <div className="relative z-[1] mx-auto max-w-[1240px] px-5 sm:px-8">
        <div data-reveal className="max-w-[680px]">
          <h2 className="display text-[2.8rem] sm:text-[3.6rem]">Sistemas que ya están operando</h2>
          <p className="mt-5 text-[1.0625rem] leading-[1.65] text-white/70">
            Cada uno empezó como una hoja de cálculo, un grupo de mensajes o una radio. Elige un proyecto.
          </p>
        </div>

        <div data-reveal style={{ '--rd': '120ms' }} className="mt-12 grid gap-10 lg:grid-cols-[300px_1fr] lg:gap-14">
          <div role="tablist" aria-label="Proyectos" aria-orientation="vertical" className="flex gap-2 overflow-x-auto pb-2 lg:flex-col lg:overflow-visible lg:pb-0" onKeyDown={onKey}>
            {projects.map((x, i) => (
              <button key={x.id} ref={(el) => { tabs.current[i] = el; }} role="tab" id={`tab-${x.id}`}
                      aria-selected={i === sel} aria-controls={`panel-${x.id}`} tabIndex={i === sel ? 0 : -1}
                      onClick={() => setSel(i)} data-testid={`project-tab-${x.id}`}
                      className={`shrink-0 rounded-[8px] border px-4 py-3.5 text-left transition-colors duration-150 lg:w-full ${
                        i === sel ? 'border-white/80 bg-white/[0.07]' : 'border-white/10 hover:border-white/35'
                      }`}>
                <span className="block text-[13px] text-white/55">{x.client}</span>
                <span className="display-2 mt-0.5 block text-[1.35rem] text-white">{x.name}</span>
                <span className="mt-1 block text-[13px] text-white/55">{x.country}, {x.year}</span>
              </button>
            ))}
          </div>

          <div key={p.id} role="tabpanel" id={`panel-${p.id}`} aria-labelledby={`tab-${p.id}`} className="tab-panel min-w-0" data-testid={`project-${p.id}`}>
            <p className="text-[1.0625rem] font-semibold text-[#9CC8EA]">{p.kind}</p>
            <div className="mt-5">{SCREENS[p.ui] && <Screens key={p.id} screens={SCREENS[p.ui]} label={`Pantallas de ${p.name}`} />}</div>
            <div className="mt-8 grid gap-6 sm:grid-cols-2">
              <div>
                <p className="text-[14px] font-semibold text-white/50">Antes</p>
                <p className="mt-1 text-[15px] leading-[1.6] text-white/75">{p.before}</p>
              </div>
              <div>
                <p className="text-[14px] font-semibold text-[#9DD48C]">Ahora</p>
                <p className="mt-1 text-[15px] leading-[1.6] text-white/90">{p.after}</p>
              </div>
            </div>
            <dl className="mt-8 grid grid-cols-3 gap-4 border-t border-white/15 pt-6">
              {p.metrics.map((m) => (
                <div key={m.label} className="min-w-0">
                  <dt className="sr-only">{m.label}</dt>
                  <dd className="display-2 num text-[1.9rem] leading-none">{m.value}</dd>
                  <dd className="mt-2 text-[13px] leading-snug text-white/60">{m.label}</dd>
                </div>
              ))}
            </dl>
            {p.link && (
              <a href={p.link} target="_blank" rel="noopener noreferrer"
                 className="mt-7 inline-flex items-center gap-2 text-[15px] font-semibold text-white underline decoration-white/30 underline-offset-4 hover:decoration-white">
                Ver el sistema en producción <ExternalLink size={15} aria-hidden />
              </a>
            )}
          </div>
        </div>

        {/* v1.2.0 · Llamada a la acción intermedia, justo después de la prueba */}
        <div className="mt-20 flex flex-col gap-6 border-t border-white/15 pt-12 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-[560px]">
            <h3 className="display-2 text-[2rem] sm:text-[2.4rem]">¿Tu operación se parece a alguno de estos casos?</h3>
            <p className="mt-3 text-[16px] leading-[1.6] text-white/70">
              Cuéntanos cómo trabaja hoy tu equipo y te decimos qué se puede automatizar primero.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <a href="#contacto" className="btn-go justify-center text-[16px]" data-testid="projects-cta">Cotizar mi proyecto</a>
            <a href={contactData.whatsappLink} target="_blank" rel="noopener noreferrer"
               className="btn-quiet justify-center !border-white/25 !bg-transparent text-[16px] !text-white">
              <MessageCircle size={18} className="text-[#5BD07A]" aria-hidden /> Escribir por WhatsApp
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
